import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Button, TextField, Typography } from "@platform/design-system";
import { ServiceWaitingPeriodType as ServiceWaitingPeriodEnum } from "@platform/openapi-types";
import { CustomCellEditorProps } from "ag-grid-react";
import {
  FC,
  type KeyboardEvent as ReactKeyboardEvent,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { ServiceWaitingPeriod } from "../../../../types/rfp.api.types";
import { SERVICE_TYPE_LABELS } from "../constants";

interface ServiceWaitingPeriodsEditorProps extends CustomCellEditorProps {
  linkedFields?: string[];
  value: ServiceWaitingPeriod[] | number | null | undefined;
  ref: Ref<{ getValue: () => ServiceWaitingPeriod[] }>;
}

const ALL_SERVICE_TYPES = Object.values(ServiceWaitingPeriodEnum);

type Mode = "NONE" | "CUSTOM";

interface SelectionState {
  type: ServiceWaitingPeriodEnum;
  months: number | "";
}

interface ServiceWaitingPeriodsModeToggleProps {
  mode: Mode;
  onModeChange: (mode: Mode) => void;
}

interface ServiceWaitingPeriodsCustomFormProps {
  serviceItems: SelectionState[];
  canAddService: boolean;
  getAvailableTypesForRow: (index: number) => ServiceWaitingPeriodEnum[];
  onServiceTypeChange: (index: number, type: ServiceWaitingPeriodEnum) => void;
  onMonthsChange: (index: number, value: string) => void;
  onDeleteService: (index: number) => void;
  onAddService: VoidFunction;
  onEnterKey: (event: ReactKeyboardEvent) => void;
}

const ServiceWaitingPeriodsModeToggle: FC<
  ServiceWaitingPeriodsModeToggleProps
> = ({ mode, onModeChange }) => (
  <ToggleButtonGroup
    value={mode}
    exclusive
    onChange={(_, value: Mode | null) => {
      if (value) {
        onModeChange(value);
      }
    }}
    size="small"
    sx={{
      my: 3,
      "& .MuiToggleButton-root": {
        color: "brandPrimary.main",
        px: 2,
      },
      "& .MuiToggleButton-root.Mui-selected": {
        bgcolor: "brandPrimary.main",
        boxShadow: 1,
        color: "white",
      },
      "& .MuiToggleButton-root.Mui-selected:hover": {
        bgcolor: "brandPrimary.dark",
      },
    }}
  >
    <ToggleButton value="NONE">None</ToggleButton>
    <ToggleButton value="CUSTOM">Custom</ToggleButton>
  </ToggleButtonGroup>
);

const ServiceWaitingPeriodsCustomForm: FC<
  ServiceWaitingPeriodsCustomFormProps
> = ({
  serviceItems,
  canAddService,
  getAvailableTypesForRow,
  onServiceTypeChange,
  onMonthsChange,
  onDeleteService,
  onAddService,
  onEnterKey,
}) => (
  <>
    <Stack spacing={1} mb={2}>
      <Stack direction="row" spacing={2} pr={8}>
        <Typography flex={1} variant="body-large">
          Service Type
        </Typography>
        <Typography variant="body-large">Months</Typography>
      </Stack>
      {serviceItems.map((item, index) => {
        const availableTypes = getAvailableTypesForRow(index);

        return (
          <Stack key={item.type} direction="row" spacing={2}>
            <Select
              fullWidth
              size="small"
              label="Service"
              value={item.type}
              onChange={(event) => {
                event.stopPropagation();
                onServiceTypeChange(
                  index,
                  event.target.value as ServiceWaitingPeriodEnum,
                );
              }}
            >
              {availableTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {SERVICE_TYPE_LABELS[type] || type}
                </MenuItem>
              ))}
            </Select>

            <TextField
              type="number"
              size="sm"
              value={item.months}
              onChange={(event) => {
                onMonthsChange(index, event.target.value);
              }}
              onKeyDown={onEnterKey}
              slotProps={{
                htmlInput: {
                  min: 1,
                  max: 24,
                  inputMode: "numeric",
                },
              }}
              sx={{ width: 110 }}
            />

            <IconButton
              onClick={() => onDeleteService(index)}
              disabled={serviceItems.length < 2}
              color="error"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        );
      })}
    </Stack>
    <Box mt={3}>
      <Button
        fullWidth
        onClick={onAddService}
        variant="secondary"
        disabled={!canAddService}
      >
        + Add Service
      </Button>
    </Box>
  </>
);

/**
 * Custom cell editor for Service Waiting Periods in Dental PPO.
 * Displays a popover with checkboxes for each service type and
 * number inputs to specify the waiting period in months.
 */
export const ServiceWaitingPeriodsEditor: FC<
  ServiceWaitingPeriodsEditorProps
> = (props) => {
  const { stopEditing, api, node, column, ref } = props;
  const linkedFields = props.linkedFields ?? [];

  const initialValue: ServiceWaitingPeriod[] = Array.isArray(props.value)
    ? props.value
    : [];
  const [mode, setMode] = useState<Mode>(
    initialValue.length > 0 ? "CUSTOM" : "NONE",
  );

  const [serviceItems, setServiceItems] = useState<SelectionState[]>(() => {
    const initialItems: SelectionState[] = [];

    initialValue.forEach((period) => {
      if (ALL_SERVICE_TYPES.includes(period.type)) {
        initialItems.push({
          type: period.type,
          months: period.months ?? 1,
        });
      }
    });

    if (initialItems.length > 0) {
      return initialItems;
    }

    return [{ type: ALL_SERVICE_TYPES[0], months: 1 }];
  });

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Expose getValue method to AG Grid
  useImperativeHandle(ref, () => ({
    getValue: () => {
      if (mode === "NONE") {
        return [];
      }

      return serviceItems.map((item) => ({
        type: item.type,
        months: item.months || 0,
      }));
    },
  }));

  // Auto-open popover when editor is created
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = (): void => {
    setOpen(false);
    if (stopEditing) {
      setTimeout(() => stopEditing(), 0);
    }
  };

  const handleDone = (): void => {
    const finalValue =
      mode === "NONE"
        ? []
        : serviceItems.map((item) => ({
            type: item.type,
            months: item.months,
          }));

    if (node && column) {
      node.setDataValue(column.getColId(), finalValue);
    }

    linkedFields.forEach((field: string) => {
      const linkedNode = api.getRowNode(field);
      if (linkedNode) {
        linkedNode.setDataValue(column.getColId(), finalValue);
      }
    });

    if (stopEditing) {
      stopEditing();
    }

    if (api && node) {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    }

    setOpen(false);
  };

  const handleServiceTypeChange = (
    index: number,
    type: ServiceWaitingPeriodEnum,
  ): void => {
    setServiceItems((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, type } : item,
      ),
    );
  };

  const handleMonthsChange = (index: number, value: string): void => {
    const isEmpty = value === "";
    let months: number | "";
    if (isEmpty) {
      months = "";
    } else {
      const parsedValue = parseInt(value, 10);
      if (isNaN(parsedValue)) {
        return;
      }
      months = Math.min(Math.max(parsedValue, 1), 24);
    }

    setServiceItems((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              months,
            }
          : item,
      ),
    );
  };

  const handleAddService = (): void => {
    const selectedTypes = new Set(serviceItems.map((item) => item.type));
    const nextType = ALL_SERVICE_TYPES.find((type) => !selectedTypes.has(type));

    if (!nextType) {
      return;
    }

    setServiceItems((prev) => [...prev, { type: nextType, months: 1 }]);
  };

  const handleDeleteService = (index: number): void => {
    if (serviceItems.length <= 1) {
      return;
    }

    setServiceItems((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const getAvailableTypesForRow = (
    index: number,
  ): ServiceWaitingPeriodEnum[] => {
    const selectedByOtherRows = new Set(
      serviceItems
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item) => item.type),
    );

    return ALL_SERVICE_TYPES.filter((type) => !selectedByOtherRows.has(type));
  };

  const canAddService = serviceItems.length < ALL_SERVICE_TYPES.length;
  const isUpdateDisabled =
    mode === "CUSTOM" &&
    serviceItems.some(
      (item) => item.months === "" || item.months < 1 || item.months > 24,
    );

  const handleEnterKey = (event: ReactKeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === "Enter" && !isUpdateDisabled) {
      event.preventDefault();
      handleDone();
    }
  };

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 400,
              maxWidth: 500,
              maxHeight: 600,
              overflow: "auto",
            },
          },
        }}
      >
        <Box
          sx={{ p: 2 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleEnterKey}
        >
          <Typography variant="body-large">Service Waiting Periods</Typography>

          <ServiceWaitingPeriodsModeToggle mode={mode} onModeChange={setMode} />

          {mode === "CUSTOM" && (
            <ServiceWaitingPeriodsCustomForm
              serviceItems={serviceItems}
              canAddService={canAddService}
              getAvailableTypesForRow={getAvailableTypesForRow}
              onServiceTypeChange={handleServiceTypeChange}
              onMonthsChange={handleMonthsChange}
              onDeleteService={handleDeleteService}
              onAddService={handleAddService}
              onEnterKey={handleEnterKey}
            />
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1} mt={3}>
            <Button onClick={handleClose} variant="tertiary">
              Cancel
            </Button>
            <Button
              onClick={handleDone}
              variant="primary"
              disabled={
                mode === "CUSTOM" &&
                serviceItems.some(
                  (item) =>
                    item.months === "" || item.months < 1 || item.months > 24,
                )
              }
            >
              Update
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

ServiceWaitingPeriodsEditor.displayName = "ServiceWaitingPeriodsEditor";
